import { useConnectionStore } from 'src/context/ConnectionContext';
import { useAppStore } from '../context/AppContext';
import { AppFeatureParams } from '../types/AppFeatureParams';
import { AppFeature } from '../types/ViewModel/AppFeature/AppFeature';
import { isDateSameOrAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { OfflineLevelEnum } from 'src/enums/offlineLevelEnum';

export default () => {
    const { appFeatureSettings } = useAppStore();
    const { offlineLevel } = useConnectionStore();

    const getAppFeatureFromDate = (appFeatureParams: AppFeatureParams) => {
        if (isFeatureEnabled(appFeatureParams)) {
            return appFeatureSettings.find((a) => a.appFeatureId === appFeatureParams.featureEnum)?.fromDate;
        }
    };

    const getAppFeatureFromDateForVessel = (appFeatureParams: AppFeatureParams): Date | undefined => {
        if (appFeatureSettings && appFeatureSettings.length > 0 && appFeatureParams.featureEnum) {
            const settingsForFeature = appFeatureSettings.find((a) => a.appFeatureId === appFeatureParams.featureEnum);
            if (!settingsForFeature) return undefined;
            if (settingsForFeature.vessels && settingsForFeature.vessels.length > 0 && appFeatureParams.imoNumber) {
                const appFeatureVessel =
                    appFeatureParams.imoNumber && settingsForFeature?.vessels?.find((v) => v.imoNumber === appFeatureParams.imoNumber.toString());
                // AppFeature configuration uses vessel-specific release dates
                if (appFeatureVessel && appFeatureVessel.fromDate) {
                    return appFeatureVessel.fromDate;
                }
                // If vessel is not in the vessel-specific list, fall through to global fromDate
            }
            // Fall back to global configuration
            return settingsForFeature.fromDate;
        }
        return undefined;
    };

    const isFeatureEnabled = (appFeatureParams: AppFeatureParams): boolean => {
        if (appFeatureSettings && appFeatureSettings.length > 0 && appFeatureParams.featureEnum) {
            const settingsForFeature = appFeatureSettings.find((a) => a.appFeatureId === appFeatureParams.featureEnum);

            if (isFeatureEnabledWhenOffline(settingsForFeature) && settingsForFeature) {
                if (settingsForFeature.isForcedEnabled) {
                    return true;
                }
                if (settingsForFeature.isForcedDisabled) {
                    return false;
                }
                if (settingsForFeature.imoNumbers && settingsForFeature.imoNumbers.length > 0 && appFeatureParams.imoNumber) {
                    const appFeatureVessel =
                        appFeatureParams.imoNumber && settingsForFeature?.vessels?.find((v) => v.imoNumber === appFeatureParams.imoNumber.toString());
                    // AppFeature configuration uses vessel-specific release dates
                    if (appFeatureVessel && appFeatureVessel.fromDate) {
                        // If no date is provided, assume current context doesn't require date validation
                        if (!appFeatureParams.date) {
                            return true;
                        }
                        return isDateSameOrAfter(appFeatureParams.date, appFeatureVessel.fromDate);
                    }
                    // If vessel is not in the vessel-specific list, fall through to global fromDate check
                }

                if (settingsForFeature.fromDate && appFeatureParams.date) {
                    if (!isFeatureEnabledAfterDate(settingsForFeature, appFeatureParams.date)) return false;
                }
                if (settingsForFeature.roles && appFeatureParams.role) {
                    if (!isFeatureEnabledForRole(settingsForFeature, appFeatureParams.role)) return false;
                }

                return true;
            }
        }
        return false;
    };

    const isFeatureEnabledWhenOffline = (appFeature: AppFeature) => {
        if (offlineLevel === OfflineLevelEnum.Online) return true;

        return !appFeature.disableWhenOffline;
    };

    const isFeatureEnabledForRole = (settingsForFeature: AppFeature, role: string): boolean => {
        if (appFeatureSettings) {
            const isfeatureEnabledForRole = settingsForFeature?.roles.includes(role);
            if (isfeatureEnabledForRole) {
                return true;
            }
        }
        return false;
    };

    const isFeatureEnabledAfterDate = (settingsForFeature: AppFeature, date: Date): boolean => {
        if (settingsForFeature) {
            const isfeatureEnabledAfterDate = isDateSameOrAfter(date, settingsForFeature?.fromDate);
            if (isfeatureEnabledAfterDate) {
                return true;
            }
        }
        return false;
    };

    return {
        isFeatureEnabled,
        getAppFeatureFromDate,
        getAppFeatureFromDateForVessel,
    };
};
